import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import Button from '~/components/atom/Button';
import CloseButton from '~/components/atom/CloseButton';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body, Heading4, Variant } from '~/components/atom/Typography';
import Portal from '~/components/molecule/Portal';
import useKeybinding from '~/hooks/useKeybinding';
import TEST_ID from './index.testid';

const text = {
  header: 'Nieuwe kenmerken',
  description:
    'Bekijk elk nieuw kenmerk. Vul een tag in of laat het veld leeg, en sla daarna op.',
  progress: (current: number, total: number) => `${current} van ${total}`,
  previous: 'Vorige',
  next: 'Volgende',
  done: 'Klaar',
};

export type Props = {
  dataTestId?: string;
  missingKenmerkIds: Array<number>;
  onActiveKenmerkIdChange: (kenmerkId: number | null) => void;
  onClose: () => void;
};

const MissingKenmerkenWizard: React.FCC<Props> = ({
  dataTestId = TEST_ID.CONTAINER,
  missingKenmerkIds,
  onActiveKenmerkIdChange,
  onClose,
}) => {
  const [stepIndex, setStepIndex] = useState(0);
  const total = missingKenmerkIds.length;
  const clampedIndex = total === 0 ? 0 : Math.min(stepIndex, total - 1);
  const currentKenmerkId = missingKenmerkIds[clampedIndex] ?? null;
  const isLastStep = clampedIndex >= total - 1;

  useKeybinding({
    keys: 'esc',
    callback: onClose,
  });

  useEffect(() => {
    if (stepIndex !== clampedIndex) {
      setStepIndex(clampedIndex);
    }
  }, [clampedIndex, stepIndex]);

  useEffect(() => {
    onActiveKenmerkIdChange(currentKenmerkId);
  }, [currentKenmerkId, onActiveKenmerkIdChange]);

  useEffect(
    () => () => {
      onActiveKenmerkIdChange(null);
    },
    [onActiveKenmerkIdChange],
  );

  if (total === 0) return null;

  return (
    <Portal root="missing-kenmerken-wizard">
      <Panel data-testid={dataTestId}>
        <CloseButton
          data-testid={TEST_ID.CLOSE_BUTTON}
          onClick={onClose}
          aria-label="Sluiten"
        />
        <JustificationContainer direction="column" gap="s" width="100%">
          <Heading4 margin={[null]} variant={Variant.primary}>
            {text.header}
          </Heading4>
          <Body size="s" margin={[null]}>
            {text.description}
          </Body>
          <Body size="s" margin={[null]} fontWeight="semiBold">
            {text.progress(clampedIndex + 1, total)}
          </Body>
          <JustificationContainer direction="column" gap="xs" width="100%">
            <FullWidthButton
              label={text.previous}
              appearance="secondary"
              ghost
              size="small"
              disabled={clampedIndex === 0}
              onClick={() => setStepIndex(index => Math.max(0, index - 1))}
              dataTestId={TEST_ID.PREVIOUS_BUTTON}
            />
            <FullWidthButton
              label={isLastStep ? text.done : text.next}
              appearance="secondary"
              size="small"
              onClick={() => {
                if (isLastStep) {
                  onClose();
                  return;
                }
                setStepIndex(index => Math.min(total - 1, index + 1));
              }}
              dataTestId={TEST_ID.NEXT_BUTTON}
            />
          </JustificationContainer>
        </JustificationContainer>
      </Panel>
    </Portal>
  );
};

const Panel = styled.div(
  ({ theme }) => css`
    position: fixed;
    left: 10%;
    top: 50%;
    transform: translateY(-50%);
    width: 200px;
    z-index: ${theme.getTokens().zIndex.portal};
    background: ${theme.color('white')};
    border-radius: ${theme.getTokens().border.radius.m};
    box-shadow: ${theme.getTokens().boxShadow.modal};
    padding: ${theme.space('m')};
  `,
);

const FullWidthButton = styled(Button)(
  () => css`
    width: 100%;
  `,
);

export default MissingKenmerkenWizard;
